import React from 'react';
import { Button, ExternalLink, FontIcon, HoverAnimation, HTMLParser, SvgIcon } from '@gik/ui';
import { InkindPageAPIModel } from '@gik/core/models/gik/InkindPage';
import bemBlock from '@gik/core/utils/bemBlock';
import { openPremiumPageUpgradeModal, openShareInkindModal } from '@gik/inkind-page';
import { useUserStore } from '@gik/core/store/UserStore';
import { isInkindPageOwner, isInkindPageOwnerOrOrganizer } from '@gik/inkind-page/utils/inkindPagePermissions';
import isInkindPageRecipientInfoComplete from '@gik/inkind-page/utils/isInkindPageRecipientInfoComplete';
import noop from '@gik/core/utils/noop';
import { openPremiumPageThankYouModal } from '@gik/checkout';

export interface IPageCardProps {
  inkindPage: InkindPageAPIModel;
  onRemove(inkindPage: InkindPageAPIModel): void | Promise<void>;
  revalidateInkindPages?(): void;
}

const blockName = 'user-page-card';

// TODO consider merging this (or this one uses the other ui component + a toolbar)
//  and the new card we use for displaying featured pages on the homepage
//  we should try use a single design for these two (with this one having a bit more admin functionality).
function UserPageCard({ onRemove, inkindPage, revalidateInkindPages = noop }: IPageCardProps): JSX.Element {
  const bem = bemBlock(blockName);

  const userStore = useUserStore();
  const isOrganizer = isInkindPageOwnerOrOrganizer(inkindPage, userStore.id);
  const isOwner = isInkindPageOwner(inkindPage, userStore.id);

  const onClickRemove = async function (e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();

    onRemove(inkindPage);
  };

  return (
    <>
      <HoverAnimation className={bem('hover-wrapper')}>
        <ExternalLink className={bem(null, [{ admin: isOrganizer }])} href={inkindPage.url}>
          <section className={bem('image-container')}>
            <div
              className={bem('page-image')}
              style={inkindPage.thumb_medium ? { backgroundImage: `url(${inkindPage.thumb_medium})` } : {}}
            />
          </section>

          <section className={bem('content')}>
            <section className={bem('title')}>
              <HTMLParser
                rawHtml={inkindPage.title.length > 75 ? inkindPage.title.substr(0, 72) + '...' : inkindPage.title}
              />
            </section>

            {isInkindPageRecipientInfoComplete(inkindPage) && (
              <section className={bem('location')}>{inkindPage.location}</section>
            )}
          </section>

          <section className={bem('buttons')}>
            <Button
              type="button"
              variant="default-plain"
              circle
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                openShareInkindModal(
                  {
                    isOrganizer,
                    pageTitle: inkindPage.title,
                    pageShortUrl: inkindPage.shortUrl,
                  },
                  'profilePage',
                  inkindPage.url
                );
              }}
              tooltip="Share page"
            >
              <FontIcon name="forward" />
            </Button>
            <Button
              type="button"
              variant="default-plain"
              circle
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                if (!inkindPage.isPremium) {
                  openPremiumPageUpgradeModal({
                    initiatedOn: 'profilePage',
                    variant: 'generic',
                    inkindRouteId: inkindPage.routeId,
                    onPurchase: revalidateInkindPages,
                  });
                } else {
                  openPremiumPageThankYouModal();
                }
              }}
              tooltip={inkindPage.isPremium ? 'Premium page' : 'Upgrade page'}
            >
              <SvgIcon className={bem('premium-star')} name={inkindPage.isPremium ? 'Star' : 'StarOutline'} />
            </Button>
            <Button
              type="button"
              variant="default-plain"
              circle
              onClick={onClickRemove}
              tooltip={isOwner ? 'Delete page' : 'Unfollow page'}
            >
              <FontIcon name={isOwner ? 'trash' : 'close'} />
            </Button>
          </section>
        </ExternalLink>
      </HoverAnimation>
    </>
  );
}

export default UserPageCard;
